import { Link, useLocation } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";

const footerLinks = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about" },
  { label: "The Visionary", href: "/visionary" },
  { label: "Listings", href: "/listings" },
  { label: "Services", href: "/#services" },
  { label: "Strategies", href: "/strategies" },
  { label: "AI Brain", href: "/ai-technology" },
  { label: "Pricing", href: "/#pricing" },
  { label: "Work Flow", href: "/#how-it-works" },
  { label: "FAQ", href: "/#faq" },
  { label: "Partners", href: "/partners" },
  { label: "Feasibility Report", href: "/feasibility-report" },
  { label: "Press", href: "/press" },
  { label: "Contact", href: "/contact" },
  { label: "Member Login", href: "/members" },
];

export default function Footer() {
  const location = useLocation();
  const { settings } = useSettings();

  const handleHashClick = (e: React.MouseEvent, hash: string) => {
    if (location.pathname === "/") {
      e.preventDefault();
      const el = document.getElementById(hash.replace('#', ''));
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer className="py-14 bg-foreground-950">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {settings.general.logo_url ? (
            <img
              src={settings.general.logo_url}
              alt={settings.general.site_name || 'CashMyProperty.ai'}
              className="h-8 w-auto max-w-[140px] object-contain"
            />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <span className="text-foreground-950 font-display font-bold text-sm">C</span>
            </div>
          )}
          {settings.general.show_site_name !== false && (
            <span className="font-display font-bold text-lg text-background-50">
              CashMyProperty<span className="text-primary-400">.ai</span>
            </span>
          )}
        </div>

        <p className="text-center text-background-400 text-sm max-w-xl mx-auto mb-8 leading-relaxed">
          India&apos;s first AI-powered land monetization platform. Unlock the hidden value in your
          property — without selling it.
        </p>

        {/* Nav links */}
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 mb-8">
          {footerLinks.map((link) => {
            const isHash = link.href.includes('#');
            return (
              <Link
                key={link.label}
                to={link.href}
                onClick={isHash ? (e) => handleHashClick(e, link.href) : undefined}
                className="text-sm text-background-400 hover:text-primary-400 transition-colors cursor-pointer"
              >
                {link.label}
              </Link>
            );
          })}
        </div>

        {/* Social */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[
            { icon: "ri-twitter-x-line", label: "Twitter" },
            { icon: "ri-linkedin-box-line", label: "LinkedIn" },
            { icon: "ri-instagram-line", label: "Instagram" },
            { icon: "ri-youtube-line", label: "YouTube" },
          ].map((s) => (
            <a
              key={s.label}
              href="#"
              aria-label={s.label}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-foreground-800 text-background-400 hover:bg-primary-500/20 hover:text-primary-400 transition-colors"
            >
              <i className={`${s.icon} text-lg`} />
            </a>
          ))}
        </div>

        {/* Bottom */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-primary-500/15">
          <div className="text-sm text-background-500 text-center sm:text-left">
            <p>© 2026 CashMyProperty.ai — All rights reserved.</p>
            <p className="mt-1">
              Powered by:{" "}
              <a
                href="https://digicube.net.in"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-400 hover:text-primary-300 hover:underline font-medium transition-colors"
              >
                Digicube Solutions
              </a>
            </p>
          </div>
          <div className="flex items-center gap-6">
            <Link
              to="/privacy-policy"
              className="text-sm text-background-400 hover:text-primary-400 transition-colors cursor-pointer"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-of-service"
              className="text-sm text-background-400 hover:text-primary-400 transition-colors cursor-pointer"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}